import { AccountingPeriod } from '@models/accounting-period';
import { Company } from '@models/company';
import { Contributor } from '@models/contributor';

import {
  CapitalIncomeDeclarationDeposit,
  CapitalIncomeDeclarationDepositJson
} from './capital-income-declaration-deposit';
import { CapitalIncomeTeledeclaration, CapitalIncomeTeledeclarationJson } from './capital-income-teledeclaration';
import { DeclarableCapitalIncomes, DeclarableCapitalIncomesJson } from './declarable-capital-incomes';
import { FormalityBase, FormalityBaseJson } from './formality-base';
import { FormalitySpecificDates } from './formality-specific-dates';
import { LegalFormalityStatus } from './legal-formality-status';
import { FormalitySupervisorEnum } from '../enums/formality-supervisor.enum';

export interface CapitalIncomeDeclarationJson extends FormalityBaseJson {
  declarable_capital_incomes?: DeclarableCapitalIncomesJson[];
  teledeclaration?: CapitalIncomeTeledeclarationJson;
  capital_income_declaration_deposit?: CapitalIncomeDeclarationDepositJson;
}

export class CapitalIncomeDeclaration extends FormalityBase {
  constructor(
    public id?: number,
    public formalityType?: string,
    public codeName?: string,
    public status?: string,
    public formalitySupervisor?: FormalitySupervisorEnum,
    public deliveryDate?: Date,
    public statuses?: LegalFormalityStatus[],
    public businessUnitId?: number,
    public production?: Contributor,
    public note?: string,
    public accountingPeriod?: AccountingPeriod,
    public company?: Company,
    public specificDates?: FormalitySpecificDates,
    public declarableCapitalIncomes?: DeclarableCapitalIncomes[],
    public teledeclaration?: CapitalIncomeTeledeclaration,
    public capitalIncomeDeclarationDeposit?: CapitalIncomeDeclarationDeposit
  ) {
    super(
      id,
      formalityType,
      codeName,
      status,
      formalitySupervisor,
      deliveryDate,
      statuses,
      businessUnitId,
      production,
      note,
      accountingPeriod,
      company,
      specificDates
    );
  }

  static fromJson(json: Partial<CapitalIncomeDeclarationJson>): CapitalIncomeDeclaration {
    let capitalIncomeDeclaration = new CapitalIncomeDeclaration();

    capitalIncomeDeclaration = { ...capitalIncomeDeclaration, ...FormalityBase.fromJson(json) };

    capitalIncomeDeclaration.declarableCapitalIncomes = json.declarable_capital_incomes
      ? json.declarable_capital_incomes.map((declarableCapitalIncomeJson: DeclarableCapitalIncomesJson) =>
          DeclarableCapitalIncomes.fromJson(declarableCapitalIncomeJson)
        )
      : null;

    capitalIncomeDeclaration.teledeclaration = json.teledeclaration
      ? CapitalIncomeTeledeclaration.fromJson(json.teledeclaration)
      : null;

    capitalIncomeDeclaration.capitalIncomeDeclarationDeposit = json.capital_income_declaration_deposit
      ? CapitalIncomeDeclarationDeposit.fromJson(json.capital_income_declaration_deposit)
      : null;

    return capitalIncomeDeclaration;
  }

  static toJson(capitalIncomeDeclaration: CapitalIncomeDeclaration): Partial<CapitalIncomeDeclarationJson> {
    return {
      ...FormalityBase.toJson(capitalIncomeDeclaration),

      declarable_capital_incomes: capitalIncomeDeclaration.declarableCapitalIncomes
        ? capitalIncomeDeclaration.declarableCapitalIncomes.map((declarableCapitalIncome: DeclarableCapitalIncomes) =>
            DeclarableCapitalIncomes.toJson(declarableCapitalIncome)
          )
        : null,

      teledeclaration: capitalIncomeDeclaration.teledeclaration
        ? CapitalIncomeTeledeclaration.toJson(capitalIncomeDeclaration.teledeclaration)
        : null,

      capital_income_declaration_deposit: capitalIncomeDeclaration.capitalIncomeDeclarationDeposit
        ? CapitalIncomeDeclarationDeposit.toJson(capitalIncomeDeclaration.capitalIncomeDeclarationDeposit)
        : null
    };
  }

  static toCapitalIncomeDeclarationFormalityJson(
    capitalIncomeDeclaration: CapitalIncomeDeclaration
  ): Partial<CapitalIncomeDeclarationJson> {
    return {
      formality_type: capitalIncomeDeclaration.formalityType,
      code_name: capitalIncomeDeclaration.codeName,
      specific_dates: capitalIncomeDeclaration.specificDates
        ? FormalitySpecificDates.toJson(capitalIncomeDeclaration.specificDates)
        : null
    };
  }
}
